/** Pure helpers for PendingInsuranceChangeInsuranceModal*/

export const BILLING_SECTION_ID = 5;

export type AssignInsuranceDocument = {
  fileName: string;
  fileURL: string;
  typeOfFile: string;
};

const DOCUMENT_TYPE_BY_SYSTEM_FIELD: Record<string, string> = {
  photosforinsurancecard: "Insurance Image",
  photofordemographicinfo: "Demographic Image",
};

export function resetBillingSelectionFields(sectionConfig: any) {
  const clonedConfig = JSON.parse(JSON.stringify(sectionConfig));

  const sanitizeField = (field: any): any => {
    const nextField = {
      ...field,
      defaultValue: "",
      fieldValue: "",
      selectedText: "",
    };

    if (Array.isArray(nextField.options)) {
      nextField.options = nextField.options.map((option: any) => ({
        ...option,
        isSelectedDefault: false,
      }));
    }

    if (Array.isArray(nextField.repeatFields)) {
      nextField.repeatFields = nextField.repeatFields.map((repeatField: any) =>
        sanitizeField(repeatField)
      );
    }

    if (Array.isArray(nextField.dependencyControls)) {
      nextField.dependencyControls = nextField.dependencyControls.map(
        (control: any) => ({
          ...control,
          dependecyFields: Array.isArray(control?.dependecyFields)
            ? control.dependecyFields.map((depField: any) =>
                sanitizeField(depField)
              )
            : control?.dependecyFields,
        })
      );
    }

    return nextField;
  };

  clonedConfig.fields = (clonedConfig.fields || []).map((field: any) =>
    sanitizeField(field)
  );

  if (Array.isArray(clonedConfig.dependencyControls)) {
    clonedConfig.dependencyControls = clonedConfig.dependencyControls.map(
      (control: any) => ({
        ...control,
        dependecyFields: Array.isArray(control?.dependecyFields)
          ? control.dependecyFields.map((depField: any) =>
              sanitizeField(depField)
            )
          : control?.dependecyFields,
      })
    );
  }

  return clonedConfig;
}

export function getFieldValue(fields: any[], targetNames: string[]) {
  const targetNamesSet = new Set(targetNames.map((n) => n.toLowerCase()));
  for (const field of fields || []) {
    const systemName = `${field?.systemFieldName || ""}`.toLowerCase();
    if (targetNamesSet.has(systemName)) {
      return {
        field,
        fieldValue: field?.fieldValue ?? field?.defaultValue ?? "",
        selectedText: field?.selectedText ?? "",
      };
    }
  }
  return { field: null, fieldValue: "", selectedText: "" };
}

/** First non-empty value from formState for any of the given keys (same semantics as modal helper). */
export function pickFirstNonEmptyFormStateValue(
  formState: any,
  keys: string[]
): any {
  for (const key of keys) {
    const value = formState?.[key];
    if (value !== undefined && value !== null && `${value}` !== "") {
      return value;
    }
  }
  return "";
}

export function collectFieldsDeep(fields: any[]): any[] {
  const out: any[] = [];
  const visit = (arr: any[]) => {
    for (const f of arr || []) {
      if (!f) continue;
      out.push(f);
      if (Array.isArray(f.repeatFields)) visit(f.repeatFields);
      if (Array.isArray(f.dependencyControls)) {
        for (const c of f.dependencyControls) {
          if (Array.isArray(c?.dependecyFields)) visit(c.dependecyFields);
        }
      }
      if (Array.isArray(f.repeatDependencyControls)) {
        for (const c of f.repeatDependencyControls) {
          if (Array.isArray(c?.dependecyFields)) visit(c.dependecyFields);
        }
      }
    }
  };
  visit(fields || []);
  return out;
}

export function flattenBillingSectionFieldTree(section: any): any[] {
  const roots: any[] = [...(section?.fields || [])];
  if (Array.isArray(section?.dependencyControls)) {
    for (const c of section.dependencyControls) {
      if (Array.isArray(c?.dependecyFields)) roots.push(...c.dependecyFields);
    }
  }
  return collectFieldsDeep(roots);
}

export function parseFileUploadValue(raw: any): any[] {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function resolveDocumentTypeOfFile(field: any): string | null {
  const rawName = field?.systemFieldName ?? field?.SystemFieldName ?? "";
  const normalizedSys = `${rawName}`.trim().toLowerCase().replace(/\s+/g, "");
  if (DOCUMENT_TYPE_BY_SYSTEM_FIELD[normalizedSys]) {
    return DOCUMENT_TYPE_BY_SYSTEM_FIELD[normalizedSys];
  }
  const looseSys = `${rawName}`.trim().toLowerCase();
  if (DOCUMENT_TYPE_BY_SYSTEM_FIELD[looseSys]) {
    return DOCUMENT_TYPE_BY_SYSTEM_FIELD[looseSys];
  }
  const label = `${field?.displayFieldName ?? field?.displayName ?? ""}`
    .trim()
    .toLowerCase();
  if (
    label.includes("insurance card") ||
    label.includes("photos for insurance")
  ) {
    return "Insurance Image";
  }
  if (label.includes("demographic")) {
    return "Demographic Image";
  }
  return null;
}

export function dedupeDocuments(docs: AssignInsuranceDocument[]) {
  const seen = new Set<string>();
  const out: AssignInsuranceDocument[] = [];
  for (const d of docs) {
    const key = `${d.fileURL}|${d.typeOfFile}|${d.fileName}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(d);
  }
  return out;
}

export function buildDocumentsFromFlatFields(flatFields: any[]) {
  const docs: AssignInsuranceDocument[] = [];
  for (const field of flatFields || []) {
    const typeOfFile = resolveDocumentTypeOfFile(field);
    if (!typeOfFile) continue;
    const items = parseFileUploadValue(
      field?.fieldValue ??
        field?.FieldValue ??
        field?.defaultValue ??
        field?.DefaultValue
    );
    for (const item of items) {
      const fileURL = item?.fileUrl || item?.FileURL || "";
      const fileName = item?.fileName || item?.FileName || "";
      if (fileURL && fileName) {
        docs.push({ fileName, fileURL, typeOfFile });
      }
    }
  }
  return docs;
}

/** Mirrors billing simple-field value pairing (see formValuesForApiBillingInformation). */
export function collectBillingFileFieldSnapshots(section: any): any[] {
  const rows: any[] = [];
  const pushIfHasValue = (fd: any) => {
    const raw =
      fd?.defaultValue ?? fd?.fieldValue ?? fd?.FieldValue ?? fd?.DefaultValue;
    if (raw === undefined || raw === null || raw === "") return;
    if (Array.isArray(raw) && raw.length === 0) return;
    rows.push({ ...fd, defaultValue: raw, fieldValue: raw });
  };

  for (const fieldsLevelData of section?.fields || []) {
    if (fieldsLevelData?.visible === false) continue;

    if (fieldsLevelData?.displayFieldName === "Repeat Start") {
      for (const rf of fieldsLevelData?.repeatFields || []) {
        pushIfHasValue(rf);
      }
      continue;
    }

    pushIfHasValue(fieldsLevelData);
  }

  if (Array.isArray(section?.dependencyControls)) {
    for (const c of section.dependencyControls) {
      for (const df of c?.dependecyFields || []) {
        pushIfHasValue(df);
      }
    }
  }

  return rows;
}

export function extractBillingDocumentsFromInputsSnapshot(
  inputs: any[] | undefined
) {
  const sections = (inputs || []).filter(
    (s: any) => s?.sectionId === BILLING_SECTION_ID
  );
  return dedupeDocuments(
    sections.flatMap((sec: any) => {
      const fromSerializedShape = buildDocumentsFromFlatFields(
        collectBillingFileFieldSnapshots(sec)
      );
      const fromFullTree = buildDocumentsFromFlatFields(
        flattenBillingSectionFieldTree(sec)
      );
      return [...fromSerializedShape, ...fromFullTree];
    })
  );
}

export function toNumberId(value: any): number {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (typeof value === "object") {
    const candidates = [
      value?.value,
      value?.id,
      value?.optionID,
      value?.optionDataID,
    ];
    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return 0;
}

export function buildAssignAnotherInsurancePayload(
  validatedInputs: any[],
  liveBillingInputs: any[] | undefined,
  ctx: {
    formState: any;
    fallbackBillingTypeOptions: any[];
  }
) {
  const { formState, fallbackBillingTypeOptions } = ctx;
  const gfs = (keys: string[]) =>
    pickFirstNonEmptyFormStateValue(formState, keys);

  const billingSections = (validatedInputs || []).filter(
    (section: any) => section?.sectionId === BILLING_SECTION_ID
  );

  const normalizedMatch = (left: any, right: any) =>
    `${left || ""}`.trim().toLowerCase() ===
    `${right || ""}`.trim().toLowerCase();

  const resolveBillingTypeOptionId = (
    billingTypeValue: string,
    billingTypeField: any
  ) => {
    if (!billingTypeValue) return 0;
    const billingTypeOptions = billingTypeField?.options?.length
      ? billingTypeField.options
      : fallbackBillingTypeOptions;
    const matchedOption = billingTypeOptions.find(
      (option: any) =>
        normalizedMatch(option?.value, billingTypeValue) ||
        normalizedMatch(option?.label, billingTypeValue)
    );
    const candidate =
      matchedOption?.optionID ??
      matchedOption?.id ??
      matchedOption?.optionDataID;
    const parsedCandidate = Number(candidate);
    return Number.isFinite(parsedCandidate) ? parsedCandidate : 0;
  };

  const fieldGroups: any[][] = [];
  billingSections.forEach((section: any) => {
    (section?.fields || []).forEach((field: any) => {
      const isRepeatContainer = field?.displayFieldName === "Repeat Start";
      if (
        isRepeatContainer &&
        Array.isArray(field?.repeatFields) &&
        field?.visible !== false
      ) {
        fieldGroups.push(field.repeatFields);
      }
    });
    const simpleFields = (section?.fields || []).filter(
      (field: any) =>
        field?.displayFieldName !== "Repeat Start" && field?.visible !== false
    );
    if (
      simpleFields.some(
        (field: any) =>
          `${field?.systemFieldName || ""}`.toLowerCase() === "billingtype"
      )
    ) {
      fieldGroups.push(simpleFields);
    }
  });

  const mergedDocuments = dedupeDocuments([
    ...extractBillingDocumentsFromInputsSnapshot(validatedInputs),
    ...extractBillingDocumentsFromInputsSnapshot(liveBillingInputs),
  ]);

  return fieldGroups
    .map((fields: any[]) => {
      const billingType = getFieldValue(fields, ["BillingType"]);
      const relationshipToInsured = getFieldValue(fields, [
        "RelationshipToInsured",
      ]);
      const insuranceProvider = getFieldValue(fields, [
        "InsuranceProvider",
        "InsuranceProviderID",
        "InsuranceProviderId",
      ]);
      const policyId = getFieldValue(fields, [
        "PrimaryPolicyID",
        "PolicyID",
        "PolicyId",
      ]);
      const groupId = getFieldValue(fields, [
        "PrimaryGroupID",
        "GroupID",
        "GroupId",
      ]);
      const insurancePhoneNo = getFieldValue(fields, [
        "InsurancePhone",
        "InsurancePhoneNo",
        "InsurancePhoneNumber",
      ]);

      const billingTypeValue =
        billingType.selectedText ||
        billingType.fieldValue ||
        gfs(["BillingType"]) ||
        "";
      const relationshipToInsuredValue =
        relationshipToInsured.selectedText ||
        relationshipToInsured.fieldValue ||
        gfs(["RelationshipToInsured"]) ||
        "";
      const insuranceProviderStateValue = gfs([
        "InsuranceProviderID",
        "InsuranceProviderId",
        "InsuranceProvider",
      ]);
      const insuranceProviderIdValue =
        toNumberId(insuranceProvider.fieldValue) ||
        toNumberId(insuranceProvider.selectedText) ||
        toNumberId(insuranceProvider.field?.defaultValue) ||
        toNumberId(insuranceProvider.field?.fieldValue) ||
        toNumberId(insuranceProviderStateValue);
      const policyIdValue =
        policyId.fieldValue || gfs(["PrimaryPolicyID", "PolicyID", "PolicyId"]);
      const groupIdValue =
        groupId.fieldValue || gfs(["PrimaryGroupID", "GroupID", "GroupId"]);
      const insurancePhoneNoValue =
        insurancePhoneNo.fieldValue ||
        gfs(["InsurancePhone", "InsurancePhoneNo", "InsurancePhoneNumber"]);

      const documents =
        mergedDocuments.length > 0
          ? mergedDocuments
          : buildDocumentsFromFlatFields(collectFieldsDeep(fields));

      return {
        insuranceId: resolveBillingTypeOptionId(
          billingTypeValue,
          billingType.field
        ),
        billingType: billingTypeValue,
        relationshipToInsured: relationshipToInsuredValue,
        insuranceProviderId: insuranceProviderIdValue,
        policyId: `${policyIdValue || ""}`,
        groupId: `${groupIdValue || ""}`,
        insurancePhoneNo: `${insurancePhoneNoValue || ""}`,
        documents,
      };
    })
    .filter(
      (entry: any) =>
        entry.insuranceId ||
        entry.billingType ||
        entry.insuranceProviderId ||
        entry.policyId ||
        entry.groupId ||
        entry.insurancePhoneNo ||
        (entry.documents && entry.documents.length > 0)
    );
}
